import { useSelector } from "react-redux";
import { rebateOfferAssets } from "assets/cdn";
import { RootState } from "redux/store";
import { useLocation, useNavigate } from "react-router-dom";

import {
	onOpenPopUp,
	selectWebStoreState,
	setSelectedDealIndex
} from "redux/slices";
import { useAppDispatch, useAppSelector } from "hooks";
import { useImagePath } from "hooks/useImagePath";
import useNewDay from "hooks/useNewDay";
import { useCallback, useEffect, useState } from "react";
import { useRebateAnalytics } from "components/RebateOffer/hooks/useRebateAnalytics";
import { useWebStoreContext } from "contexts";
import { offersApi, useGetFeatureTrackerQuery } from "redux/apis";
import { IShopPurchaseOfferMultiItem } from "contexts/WebStoreProvider/types";

import { RebateOfferResponse } from "interfaces/rebate-offer";
import { FeatureType } from "interfaces/feature-tracker";
import { setOpen as setMegaMenuOpen } from "redux/slices";
import { useLinks } from "hooks/useLinks";
import { sortByPriority } from "utils/sort";

export const useRebateIconLogic = (from?: string) => {
	const { getImage } = useImagePath("cdn");
	const iconUrl = getImage(rebateOfferAssets.icon);
	const location = useLocation();
	const links = useLinks();
	const isMegaMenuOpen = useSelector(
		(state: RootState) => state.megaMenu.isOpen
	);
	const isHomePage = location.pathname === links.home.path;
	const isShopPage = location.pathname === links.shop.path;
	const dispatch = useAppDispatch();
	const { newDay } = useNewDay();
	const { authToken } = useWebStoreContext();
	const { nowTime } = useWebStoreContext();
	const { rewardTypes } = useAppSelector(selectWebStoreState).store;
	const navigate = useNavigate();

	const [lastNotPurchasedSessionId, setLastNotPurchasedSessionId] = useState<
		string | null
	>(null);
	const [lastPurchasedSessionId, setLastPurchasedSessionId] = useState<
		string | null
	>(null);

	const { sessionId } = useAppSelector(selectWebStoreState);

	const {
		data: rebateOfferData,
		refetch,
		isLoading
	}: {
		data?: RebateOfferResponse;
		isLoading: boolean;
		refetch: () => void;
	} = useGetFeatureTrackerQuery(
		{
			FeatureType: FeatureType.REBATE_OFFER,
			isAuthenticated: !!authToken?.value
		},
		{
			skip: !authToken?.value,
			pollingInterval: 10 * 1000,
			refetchOnFocus: true
		}
	);

	const {
		data: featuredDealsData
	}: { data?: { offers?: IShopPurchaseOfferMultiItem[] } } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "featured",
			isAuthenticated: !!authToken?.value
		});

	const offer = featuredDealsData?.offers?.find((o) => o?.rebateConfig?.id);

	const offerIndex = sortByPriority(featuredDealsData?.offers).findIndex(
		(o) => o?.rebateConfig?.id
	);

	const rewardType = rewardTypes?.find(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(type: any) =>
			type.id === offer?.rebateConfig?.inventoryType ||
			type.id === rebateOfferData?.rebateTracker?.inventoryType
	);

	const currentProgress =
		(rebateOfferData?.rebateTracker?.targetSpend ?? 0) -
		(rebateOfferData?.rebateTracker?.amountToNextEarn ?? 0);

	const { sendAnalytics } = useRebateAnalytics({
		offer,
		rebateTracker: rebateOfferData?.rebateTracker,
		nowTime,
		rewardType,
		currentProgress,
		source: from ?? location.pathname,
		eventType: "event_icon"
	});

	const onClick = () => {
		if (!rebateOfferData?.rebateTracker) {
			if (from === "mega-menu") {
				dispatch(setMegaMenuOpen(false));
			}
			dispatch(setSelectedDealIndex(offerIndex));
			if (location.pathname !== links.shop.path) {
				navigate(links.shop.path);
			}
			return;
		}

		dispatch(onOpenPopUp(`rebate-main-popup::${"0"}`));

		//* Analytics Reporting - Click
		sendAnalytics("event_icon_click");
	};

	const handleOnEnd = () => {
		setTimeout(() => {
			refetch();
			dispatch(
				offersApi.util.invalidateTags([
					{
						type: "offersTag",
						id: "featured"
					}
				])
			);
		}, 500);
	};

	const isDisabled = useCallback(() => {
		return (
			isLoading ||
			!authToken?.value ||
			(!offer && !rebateOfferData?.rebateTracker) ||
			(offer && rebateOfferData?.rebateTracker) ||
			(from === undefined &&
				isShopPage &&
				offer &&
				!rebateOfferData?.rebateTracker) ||
			(from === undefined && (isMegaMenuOpen || isHomePage))
		);
	}, [
		isLoading,
		rebateOfferData,
		authToken,
		offer,
		from,
		isShopPage,
		isMegaMenuOpen,
		isHomePage
	]);

	// Preload IconImage
	useEffect(() => {
		const img = new window.Image();
		img.src = iconUrl;
	}, []);

	useEffect(() => {
		if (newDay) {
			refetch();
		}
	}, [newDay]);

	//* Analytics Reporting - Not purchased state - First time view per sessionId
	useEffect(() => {
		if (
			!isDisabled() &&
			offer &&
			nowTime &&
			sessionId &&
			lastNotPurchasedSessionId !== sessionId
		) {
			sendAnalytics("event_icon_view");
			setLastNotPurchasedSessionId(sessionId);
		}
	}, [offer, nowTime, isDisabled, sessionId, lastNotPurchasedSessionId]);

	//* Analytics Reporting - Purchased state - First time view per sessionId
	useEffect(() => {
		if (
			!isDisabled() &&
			rebateOfferData?.rebateTracker &&
			nowTime &&
			sessionId &&
			lastPurchasedSessionId !== sessionId
		) {
			sendAnalytics("event_icon_view");
			setLastPurchasedSessionId(sessionId);
		}
	}, [rebateOfferData, nowTime, isDisabled, sessionId, lastPurchasedSessionId]);

	return {
		isDisabled,
		onClick,
		handleOnEnd,
		rebateOfferData,
		nowTime,
		currentProgress,
		rewardType
	};
};
